import {useCallback, useEffect, useRef, useState} from 'react';
import {DropResult} from '@hello-pangea/dnd';
import {IPresentation} from '../../../../incase-domain/presentation';
import {ListParams, SetListParamsOptions} from '../../../../hooks/listParams';
import {FOLDER_DROPPABLE_PREFIX} from './FolderNav';
import {FOLDER_FILTER_ALL, FOLDER_FILTER_UNFILED, IPresentationFolder} from './types';
import {
    useCreatePresentationFolder,
    useDeletePresentationFolder,
    useMovePresentationsToFolder,
    usePresentationFoldersQuery,
    useRenamePresentationFolder,
} from './usePresentationFolders';

function resolveFolderKeyAtPoint(x: number, y: number): string | null {
    const holder: {key: string | null} = {key: null};
    document.elementsFromPoint(x, y).forEach((element) => {
        if (holder.key) return;
        const folderKey = (element as HTMLElement).closest?.('[data-folder-droppable]')?.getAttribute('data-folder-droppable');
        if (folderKey) holder.key = folderKey;
    });
    return holder.key;
}

export interface PresentationFolderView {
    folders: IPresentationFolder[];
    foldersLoading: boolean;
    selectedFolder: string;
    cursorFolderKey: string | null;
    createFolderOpen: boolean;
    folderToRename: IPresentationFolder | undefined;
    folderToDelete: IPresentationFolder | undefined;
    moveTargets: IPresentation[];
    creatingFolder: boolean;
    renamingFolder: boolean;
    movingPresentations: boolean;
    handleSelectFolder: (folder: string) => void;
    openCreateFolder: () => void;
    closeCreateFolder: () => void;
    handleCreateFolder: (name: string) => void;
    openRenameFolder: (folder: IPresentationFolder) => void;
    closeRenameFolder: () => void;
    handleRenameFolder: (name: string) => void;
    openDeleteFolder: (folder: IPresentationFolder) => void;
    closeDeleteFolder: () => void;
    handleConfirmDeleteFolder: () => void;
    handleMoveTargets: (targets: IPresentation[]) => void;
    closeMoveToFolder: () => void;
    handleMoveToFolder: (folderId: string | null) => void;
    handleDragStart: () => void;
    handleDragEnd: (result: DropResult) => void;
}

export function usePresentationFolderView(
    enabled: boolean,
    listParams: ListParams,
    setListParams: (partial: Partial<ListParams>, options?: SetListParamsOptions) => void
): PresentationFolderView {
    const foldersQuery = usePresentationFoldersQuery(enabled);
    const folders = foldersQuery.data ?? [];
    const selectedFolder = listParams.filters?.folder || FOLDER_FILTER_ALL;

    const [createFolderOpen, setCreateFolderOpen] = useState(false);
    const [folderToRename, setFolderToRename] = useState<IPresentationFolder | undefined>();
    const [folderToDelete, setFolderToDelete] = useState<IPresentationFolder | undefined>();
    const [moveTargets, setMoveTargets] = useState<IPresentation[]>([]);
    const [cursorFolderKey, setCursorFolderKey] = useState<string | null>(null);
    const cursorPositionRef = useRef<{x: number; y: number} | null>(null);
    const releasePositionRef = useRef<{x: number; y: number} | null>(null);

    const createFolderMutation = useCreatePresentationFolder((folderId) => {
        setCreateFolderOpen(false);
        setListParams({filters: {...listParams.filters, folder: folderId}}, {resetPage: true});
    });
    const renameFolderMutation = useRenamePresentationFolder();
    const deleteFolderMutation = useDeletePresentationFolder();
    const moveMutation = useMovePresentationsToFolder();

    const resolveFolderKeyAtCursor = (): string | null => {
        const position = cursorPositionRef.current;
        if (!position) return null;
        return resolveFolderKeyAtPoint(position.x, position.y);
    };

    const resolveFolderKeyAtReleasePoint = (): string | null => {
        const position = releasePositionRef.current;
        if (!position) return null;
        return resolveFolderKeyAtPoint(position.x, position.y);
    };

    const resolveFolderKeyFromDestination = (result: DropResult): string | null => {
        const destinationId = result.destination?.droppableId;
        if (!destinationId || !destinationId.startsWith(FOLDER_DROPPABLE_PREFIX)) return null;
        return destinationId.slice(FOLDER_DROPPABLE_PREFIX.length);
    };

    const handleCursorTrack = useCallback((event: MouseEvent): void => {
        cursorPositionRef.current = {x: event.clientX, y: event.clientY};
        const targetKey = resolveFolderKeyAtPoint(event.clientX, event.clientY);
        setCursorFolderKey((previous) => (previous === targetKey ? previous : targetKey));
    }, []);

    const handleReleaseTrack = useCallback((event: MouseEvent | TouchEvent): void => {
        if (event instanceof MouseEvent) {
            releasePositionRef.current = {x: event.clientX, y: event.clientY};
        } else if (event.changedTouches.length > 0) {
            releasePositionRef.current = {x: event.changedTouches[0].clientX, y: event.changedTouches[0].clientY};
        }
    }, []);

    useEffect(() => {
        return () => {
            window.removeEventListener('mousemove', handleCursorTrack);
            window.removeEventListener('mouseup', handleReleaseTrack, true);
            window.removeEventListener('touchend', handleReleaseTrack, true);
        };
    }, [handleCursorTrack, handleReleaseTrack]);

    const handleSelectFolder = (folder: string): void => {
        setListParams({filters: {...listParams.filters, folder}}, {resetPage: true});
    };

    const openCreateFolder = (): void => {
        setCreateFolderOpen(true);
    };

    const closeCreateFolder = (): void => {
        setCreateFolderOpen(false);
    };

    const handleCreateFolder = (name: string): void => {
        createFolderMutation.mutate(name);
    };

    const openRenameFolder = (folder: IPresentationFolder): void => {
        setFolderToRename(folder);
    };

    const closeRenameFolder = (): void => {
        setFolderToRename(undefined);
    };

    const handleRenameFolder = (name: string): void => {
        if (!folderToRename) return;
        renameFolderMutation.mutate({folderId: folderToRename._id, name}, {onSuccess: () => setFolderToRename(undefined)});
    };

    const openDeleteFolder = (folder: IPresentationFolder): void => {
        setFolderToDelete(folder);
    };

    const closeDeleteFolder = (): void => {
        setFolderToDelete(undefined);
    };

    const handleConfirmDeleteFolder = (): void => {
        if (!folderToDelete) return;
        deleteFolderMutation.mutate(folderToDelete._id, {
            onSuccess: () => {
                if (selectedFolder === folderToDelete._id) {
                    setListParams({filters: {...listParams.filters, folder: FOLDER_FILTER_ALL}}, {resetPage: true});
                }
                setFolderToDelete(undefined);
            },
        });
    };

    const handleMoveTargets = (targets: IPresentation[]): void => {
        setMoveTargets(targets);
    };

    const closeMoveToFolder = (): void => {
        setMoveTargets([]);
    };

    const handleMoveToFolder = (folderId: string | null): void => {
        if (moveTargets.length === 0) return;
        moveMutation.mutate({presentationIds: moveTargets.map((item) => item._id), folderId}, {onSuccess: () => setMoveTargets([])});
    };

    const handleDragEnd = (result: DropResult): void => {
        window.removeEventListener('mousemove', handleCursorTrack);
        window.removeEventListener('mouseup', handleReleaseTrack, true);
        window.removeEventListener('touchend', handleReleaseTrack, true);
        const targetKey = resolveFolderKeyAtReleasePoint() ?? resolveFolderKeyAtCursor() ?? resolveFolderKeyFromDestination(result);
        setCursorFolderKey(null);
        cursorPositionRef.current = null;
        releasePositionRef.current = null;
        if (!targetKey) return;
        const folderId = targetKey === FOLDER_FILTER_ALL || targetKey === FOLDER_FILTER_UNFILED ? null : targetKey;
        moveMutation.mutate({presentationIds: [result.draggableId], folderId});
    };

    const handleDragStart = (): void => {
        cursorPositionRef.current = null;
        releasePositionRef.current = null;
        setCursorFolderKey(null);
        window.addEventListener('mousemove', handleCursorTrack);
        window.addEventListener('mouseup', handleReleaseTrack, true);
        window.addEventListener('touchend', handleReleaseTrack, true);
    };

    useEffect(() => {
        if (foldersQuery.isLoading || foldersQuery.isError) return;
        if (
            selectedFolder !== FOLDER_FILTER_ALL &&
            selectedFolder !== FOLDER_FILTER_UNFILED &&
            !folders.some((folder) => folder._id === selectedFolder)
        ) {
            setListParams({filters: {...listParams.filters, folder: FOLDER_FILTER_ALL}}, {replace: true});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [foldersQuery.isLoading, foldersQuery.isError, folders, selectedFolder]);

    return {
        folders,
        foldersLoading: foldersQuery.isLoading,
        selectedFolder,
        cursorFolderKey,
        createFolderOpen,
        folderToRename,
        folderToDelete,
        moveTargets,
        creatingFolder: createFolderMutation.isLoading,
        renamingFolder: renameFolderMutation.isLoading,
        movingPresentations: moveMutation.isLoading,
        handleSelectFolder,
        openCreateFolder,
        closeCreateFolder,
        handleCreateFolder,
        openRenameFolder,
        closeRenameFolder,
        handleRenameFolder,
        openDeleteFolder,
        closeDeleteFolder,
        handleConfirmDeleteFolder,
        handleMoveTargets,
        closeMoveToFolder,
        handleMoveToFolder,
        handleDragStart,
        handleDragEnd,
    };
}
